import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useResetPasswordForm } from "../../hook/useResetPasswordForm";
import {
  resetPasswordSchema,
  ResetPasswordType,
} from "../../schemas/resetPassword";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  Box,
  Typography,
  useForm,
  FormMessage,
  Spinner,
} from "@orion/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import PasswordValidationTags from "../../components/PasswordValidationTags";
import { PasswordCriteriaKey } from "../../types";

const ResetTemporaryPasswordScreen = () => {
  const { t } = useTranslation();
  const {
    submitResetTempPsw: submit,
    loading,
    passwordCriteria,
  } = useResetPasswordForm();

  const schema = resetPasswordSchema(t);
  const form = useForm<ResetPasswordType>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: "", confirmNewPassword: "" },
  });

  const { errors, dirtyFields, isValid } = form.formState;
  const passwordValue = form.watch("newPassword", "");
  const confirmPasswordValue = form.watch("confirmNewPassword", "");

  const checkValidation: Record<PasswordCriteriaKey, boolean> = {
    length: passwordValue.length >= 8 && passwordValue.length <= 20,
    lowercase: /[a-z]/.test(passwordValue),
    uppercase: /[A-Z]/.test(passwordValue),
    number: /[0-9]/.test(passwordValue),
    special: /[!"#'()*+,-./:;<=>?@]/.test(passwordValue),
    match:
      confirmPasswordValue === passwordValue && confirmPasswordValue !== "",
  };

  return (
    <Box
      flex
      column
      my9
      data-landmark="1"
      role="form"
      style={{
        width: "576px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "40px",
        position: "relative",
        zIndex: 1,
      }}
    >
      {loading ? (
        <Spinner size={"16"} />
      ) : (
        <>
          <Box
            flex
            gap3
            column
            style={{
              width: "576px",
              height: "60px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
            aria-label={
              t("resetPassword.title") + " " + t("resetPassword.subtitle")
            }
          >
            <Typography text2xl fontBold>
              {t("resetPassword.title")}
            </Typography>
            <Typography textPrimary60>{t("resetPassword.subtitle")}</Typography>
          </Box>
          <Box style={{ width: "100%" }}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(submit)}>
                <Box
                  my15
                  flex
                  flexDirection="row"
                  gap15
                  style={{
                    minHeight: "44px",
                    padding: "0 8px",
                  }}
                >
                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem style={{ width: "272px" }}>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            aria-label={t("resetPassword.newPassword")}
                            label={t("resetPassword.newPassword")}
                            aria-required="true"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmNewPassword"
                    render={({ field }) => (
                      <FormItem style={{ width: "272px" }}>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            label={t("resetPassword.confirmNewPassword")}
                            aria-label={t("resetPassword.confirmNewPassword")}
                            aria-required="true"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </Box>
                <Box style={{ padding: "0 8px" }}>
                  <PasswordValidationTags
                    passwordCriteria={passwordCriteria}
                    checkValidation={checkValidation}
                    dirtyFields={{
                      newPassword: dirtyFields.newPassword ?? false,
                    }}
                  />
                </Box>
                <Box style={{ padding: "0 8px" }}>
                  <Box justifyItems="center">
                    <Button type="submit" minW52 isDisabled={!isValid}>
                      {t("resetPassword.confimCta")}
                    </Button>
                  </Box>
                </Box>
              </form>
            </Form>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ResetTemporaryPasswordScreen;
